(() => {
  if (window.__BH_SIZE_DATA_TRACKER_V2__) return;
  window.__BH_SIZE_DATA_TRACKER_V2__ = true;

  const ROOT_SELECTOR = '[id^="bh-size-"]';
  const PROPERTY_MARKER = 'data-bh-size-property';
  const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

  const normalize = (value) => String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  const toNumber = (value) => {
    const number = Number.parseFloat(String(value || ''));
    return Number.isFinite(number) ? number : 0;
  };
  const round = (value, decimals = 1) => {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
  };
  const makeSessionId = () => {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
    return `bh-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  };

  const rangeScore = (value, range) => {
    if (!Array.isArray(range) || range.length < 2) return 0.5;
    const minimum = Number(range[0]);
    const maximum = Number(range[1]);
    if (!Number.isFinite(minimum) || !Number.isFinite(maximum) || maximum <= minimum) return 0.5;
    if (value >= minimum && value <= maximum) return 1;
    const distance = value < minimum ? minimum - value : value - maximum;
    return Math.max(0, 1 - distance / (maximum - minimum));
  };

  const usualScore = (usualSize, configuredSizes) => {
    const list = (configuredSizes || []).map(normalize);
    if (list.includes(usualSize)) return 1;
    const usualIndex = SIZE_ORDER.indexOf(usualSize);
    if (usualIndex < 0) return 0;
    return list.some((size) => Math.abs(SIZE_ORDER.indexOf(size) - usualIndex) === 1) ? 0.6 : 0;
  };

  const portalFor = (root) => {
    const suffix = root.id.replace(/^bh-size-/, '');
    return [...document.querySelectorAll('[class^="bh-portal-"], [class*=" bh-portal-"]')].find((element) =>
      element.classList.contains(`bh-portal-${suffix}`)
    );
  };

  const pickerFor = (root) => {
    const scope = root.closest('.product-details') || root.closest('.shopify-section') || document.querySelector('main');
    return (
      scope?.querySelector('variant-picker, swatches-variant-picker-component') ||
      document.querySelector('main variant-picker, main swatches-variant-picker-component')
    );
  };

  const productFormFor = (root) => {
    const scope = root.closest('.product-details') || root.closest('.shopify-section') || document.querySelector('main');
    const localForm = scope?.querySelector('product-form-component form[data-type="add-to-cart-form"]');
    if (localForm) return localForm;
    return document.querySelector('main product-form-component form[data-type="add-to-cart-form"]');
  };

  const selectedSizeFor = (root) => {
    const picker = pickerFor(root);
    if (!picker) return '';

    const checked = [...picker.querySelectorAll('input[data-option-name]:checked')].find((input) =>
      String(input.dataset.optionName || '').toLowerCase().includes('size')
    );
    if (checked) return normalize(checked.value);

    const selectedOption = [...picker.querySelectorAll('option[data-option-name]:checked')].find((option) =>
      String(option.dataset.optionName || '').toLowerCase().includes('size')
    );
    if (selectedOption) return normalize(selectedOption.value || selectedOption.textContent);

    const sizeSelect = [...picker.querySelectorAll('select')].find((select) =>
      String(select.dataset.optionName || select.name || '').toLowerCase().includes('size')
    );
    return sizeSelect ? normalize(sizeSelect.value) : '';
  };

  const variantStatus = (root, size) => {
    const picker = pickerFor(root);
    const normalizedSize = normalize(size);
    if (!picker) return { exists: false, available: false };

    const radio = [...picker.querySelectorAll('input[data-option-name]')].find(
      (input) =>
        String(input.dataset.optionName || '').toLowerCase().includes('size') &&
        normalize(input.value) === normalizedSize
    );
    if (radio) {
      return {
        exists: true,
        available: radio.dataset.optionAvailable !== 'false' && radio.getAttribute('aria-disabled') !== 'true'
      };
    }

    const option = [...picker.querySelectorAll('option[data-option-name]')].find(
      (item) =>
        String(item.dataset.optionName || '').toLowerCase().includes('size') &&
        normalize(item.value || item.textContent) === normalizedSize
    );
    return option
      ? {
          exists: true,
          available: !option.disabled && !String(option.textContent || '').toLowerCase().includes('unavailable')
        }
      : { exists: false, available: false };
  };

  const setHiddenProperty = (form, key, value) => {
    const name = `properties[${key}]`;
    let input = form.querySelector(`input[${PROPERTY_MARKER}][name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.setAttribute(PROPERTY_MARKER, '');
      form.appendChild(input);
    }
    input.value = String(value ?? '');
  };

  const setup = (root) => {
    if (!root || root.dataset.bhSizeDataReady === '1') return;

    const portal = portalFor(root);
    const dataElement = root.querySelector('.bh-data');
    if (!portal || !dataElement) return;

    let data = {};
    try {
      data = JSON.parse(dataElement.textContent || '{}');
    } catch (error) {
      console.warn('[BH size data] Unable to read size chart data.', error);
      return;
    }

    root.dataset.bhSizeDataReady = '1';

    const recommendation = data.recommendation || {};
    const sizes = recommendation.sizes || {};
    let lastRecord = null;

    const inputValue = (selector) => toNumber(portal.querySelector(selector)?.value);

    const readInputs = () => {
      const unit = portal.querySelector('.bh-input-unit .on')?.dataset.unit === 'metric' ? 'metric' : 'imperial';
      const feet = inputValue('.bh-ft');
      const inches = inputValue('.bh-inch');
      const pounds = inputValue('.bh-lb');
      const metricHeight = inputValue('.bh-hcm');
      const metricWeight = inputValue('.bh-kg');
      const heightCm = unit === 'metric' ? metricHeight : (feet * 12 + inches) * 2.54;
      const weightKg = unit === 'metric' ? metricWeight : pounds * 0.45359237;

      return {
        unit,
        heightCm,
        weightKg,
        heightEntered: unit === 'metric' ? `${metricHeight} cm` : `${feet} ft ${inches} in`,
        weightEntered: unit === 'metric' ? `${metricWeight} kg` : `${pounds} lb`,
        usualSize: normalize(portal.querySelector('.bh-usual')?.value)
      };
    };

    const buildRecord = () => {
      const inputs = readInputs();
      const { unit, heightCm, weightKg, heightEntered, weightEntered, usualSize } = inputs;
      if (!heightCm || !weightKg || !usualSize) return null;
      if (heightCm < 120 || heightCm > 230 || weightKg < 30 || weightKg > 220) return null;

      const base = {
        sessionId: makeSessionId(),
        recordedAt: new Date().toISOString(),
        heightCm: round(heightCm),
        weightKg: round(weightKg),
        heightEntered,
        weightEntered,
        inputUnit: unit,
        usualSize,
        recommendedSize: '',
        alternativeSize: '',
        resultType: '',
        confidence: '',
        modelVersion: String(recommendation.model_version || recommendation.version || '1'),
        chartVersion: String(data.size_chart_version || data.version || '1')
      };

      const unsupported = (recommendation.unsupported_usual_sizes || []).map(normalize);
      if (
        unsupported.includes(usualSize) ||
        (Number(recommendation.max_supported_height_cm) && heightCm > Number(recommendation.max_supported_height_cm)) ||
        (Number(recommendation.max_supported_weight_kg) && weightKg > Number(recommendation.max_supported_weight_kg))
      ) {
        return { ...base, resultType: 'outside_range' };
      }

      const weights = recommendation.weights || {};
      const usualWeight = Number.isFinite(Number(weights.usual_size)) ? Number(weights.usual_size) : 0.5;
      const weightWeight = Number.isFinite(Number(weights.weight)) ? Number(weights.weight) : 0.3;
      const heightWeight = Number.isFinite(Number(weights.height)) ? Number(weights.height) : 0.2;
      const totalWeight = usualWeight + weightWeight + heightWeight || 1;

      const ranked = Object.entries(sizes)
        .map(([size, configuration]) => ({
          size,
          configuration,
          status: variantStatus(root, size),
          score:
            (usualScore(usualSize, configuration.usual_sizes) * usualWeight +
              rangeScore(weightKg, configuration.weight_kg) * weightWeight +
              rangeScore(heightCm, configuration.height_cm) * heightWeight) /
            totalWeight
        }))
        .sort((a, b) => b.score - a.score);

      if (!ranked.length) return { ...base, resultType: 'no_configuration' };

      const best = ranked[0];
      const alternative = ranked[1];
      const minimumConfidence = Number.isFinite(Number(recommendation.minimum_confidence))
        ? Number(recommendation.minimum_confidence)
        : 0.58;
      const betweenThreshold = Number.isFinite(Number(recommendation.between_size_threshold))
        ? Number(recommendation.between_size_threshold)
        : 0.08;
      const confidence = round(best.score, 3);

      if (best.score < minimumConfidence) return { ...base, confidence, resultType: 'low_confidence' };
      if (!best.status.exists) return { ...base, confidence, resultType: 'size_not_offered' };

      const recommendedSize = normalize(best.size);
      if (!best.status.available) {
        return { ...base, confidence, recommendedSize, resultType: 'recommended_sold_out' };
      }

      if (alternative && best.score - alternative.score <= betweenThreshold && alternative.status.exists) {
        return {
          ...base,
          confidence,
          recommendedSize,
          alternativeSize: normalize(alternative.size),
          resultType: 'between_sizes'
        };
      }

      return { ...base, confidence, recommendedSize, resultType: 'recommended' };
    };

    const syncProperties = () => {
      if (!lastRecord) return;
      const form = productFormFor(root);
      if (!form) return;

      const selectedSize = selectedSizeFor(root);
      const followed = lastRecord.recommendedSize
        ? selectedSize
          ? selectedSize === lastRecord.recommendedSize
            ? 'yes'
            : 'no'
          : 'unknown'
        : 'not_applicable';

      const properties = {
        _bh_size_session: lastRecord.sessionId,
        _bh_height_cm: lastRecord.heightCm,
        _bh_weight_kg: lastRecord.weightKg,
        _bh_height_entered: lastRecord.heightEntered,
        _bh_weight_entered: lastRecord.weightEntered,
        _bh_input_unit: lastRecord.inputUnit,
        _bh_usual_size: lastRecord.usualSize,
        _bh_recommended_size: lastRecord.recommendedSize,
        _bh_alternative_size: lastRecord.alternativeSize,
        _bh_result_type: lastRecord.resultType,
        _bh_confidence: lastRecord.confidence,
        _bh_selected_size: selectedSize,
        _bh_followed_recommendation: followed,
        _bh_size_model_version: lastRecord.modelVersion,
        _bh_size_chart_version: lastRecord.chartVersion,
        _bh_recommended_at: lastRecord.recordedAt
      };

      Object.entries(properties).forEach(([key, value]) => setHiddenProperty(form, key, value));
    };

    portal.querySelector('.bh-calc')?.addEventListener('click', () => {
      setTimeout(() => {
        const record = buildRecord();
        if (!record) return;
        lastRecord = record;
        syncProperties();
      }, 0);
    });

    portal.querySelector('.bh-select')?.addEventListener('click', () => setTimeout(syncProperties, 0));
    pickerFor(root)?.addEventListener('change', () => setTimeout(syncProperties, 0));

    document.addEventListener(
      'submit',
      (event) => {
        const form = productFormFor(root);
        if (lastRecord && form && event.target === form) syncProperties();
      },
      true
    );
  };

  const initializeWithin = (scope) => {
    if (!(scope instanceof Element || scope instanceof Document)) return;
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) setup(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(setup);
  };

  const start = () => {
    initializeWithin(document);

    const scope = document.querySelector('#MainContent') || document.querySelector('main');
    if (!scope) return;

    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element) initializeWithin(node);
        }
      }
    }).observe(scope, { childList: true, subtree: true });
  };

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) initializeWithin(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
